import type React from "react";
import { useState } from "react";
import { ArrowRight, ExternalLink, CheckCircle, Star, CreditCard } from "lucide-react";
import { useTranslation } from "../../../i18n";

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface Stat {
  label: string;
  value: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
}

interface EnhancedProductTemplateProps {
  logoSrc: string;
  logoAlt: string;
  title: string;
  slogan?: string;
  description: string;
  features: Feature[];
  primaryCtaText: string;
  primaryCtaLink: string;
  secondaryCtaText: string;
  secondaryCtaLink: string;
  colorScheme?: "blue" | "green" | "purple" | "orange";
  layout?: "default" | "centered" | "split";
  showBadge?: boolean;
  badgeText?: string;
  showStats?: boolean;
  stats?: Stat[];
  showTestimonial?: boolean;
  testimonial?: Testimonial;
  showAnimation?: boolean;
}

export default function EnhancedProductTemplate({
  logoSrc,
  logoAlt,
  title,
  slogan,
  description,
  features,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  colorScheme = "blue",
  layout = "default",
  showBadge = false,
  badgeText,
  showStats = false,
  stats = [],
  showTestimonial = false,
  testimonial,
  showAnimation = false,
}: EnhancedProductTemplateProps) {
  const { t } = useTranslation();
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  const colorClasses = {
    blue: {
      gradient: "from-blue-500 to-indigo-600",
      primary: "bg-blue-600 hover:bg-blue-700",
      secondary: "border-blue-600 text-blue-600 hover:bg-blue-50",
      accent: "text-blue-600",
      badge: "bg-blue-100 text-blue-800",
    },
    green: {
      gradient: "from-green-500 to-emerald-600",
      primary: "bg-green-600 hover:bg-green-700",
      secondary: "border-green-600 text-green-600 hover:bg-green-50",
      accent: "text-green-600",
      badge: "bg-green-100 text-green-800",
    },
    purple: {
      gradient: "from-purple-500 to-indigo-600",
      primary: "bg-purple-600 hover:bg-purple-700",
      secondary: "border-purple-600 text-purple-600 hover:bg-purple-50",
      accent: "text-purple-600",
      badge: "bg-purple-100 text-purple-800",
    },
    orange: {
      gradient: "from-orange-500 to-red-600",
      primary: "bg-orange-600 hover:bg-orange-700",
      secondary: "border-orange-600 text-orange-600 hover:bg-orange-50",
      accent: "text-orange-600",
      badge: "bg-orange-100 text-orange-800",
    },
  };

  const colors = colorClasses[colorScheme];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {showBadge && badgeText && (
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full ${colors.badge} mb-6`}
            >
              <span className="text-sm font-medium">{badgeText}</span>
            </div>
          )}

          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          {slogan && (
            <p className={`text-xl ${colors.accent} mb-4 font-medium`}>
              {slogan}
            </p>
          )}
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {description}
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <a
              href={primaryCtaLink}
              className={`inline-flex items-center px-6 py-3 ${colors.primary} text-white rounded-lg transition-colors`}
            >
              {primaryCtaText}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
            <a
              href={secondaryCtaLink}
              className={`inline-flex items-center px-6 py-3 border ${colors.secondary} rounded-lg transition-colors`}
            >
              {secondaryCtaText}
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Stats */}
        {showStats && stats.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className={`text-3xl font-bold ${colors.accent} mb-2`}>
                  {stat.value}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 ${
                showAnimation ? "hover:scale-105" : ""
              }`}
              onMouseEnter={() => setHoveredFeature(index)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div
                className={`w-12 h-12 bg-gradient-to-r ${colors.gradient} rounded-lg flex items-center justify-center mb-4`}
              >
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm">{feature.description}</p>
              {hoveredFeature === index && showAnimation && (
                <div className="mt-4 flex items-center text-sm text-green-600">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {t('routes.billing.availableNow')}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Testimonial */}
        {showTestimonial && testimonial && (
          <div className="bg-gray-50 rounded-xl p-8 mb-16">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="h-5 w-5 text-yellow-400 fill-current"
                  />
                ))}
              </div>
              <blockquote className="text-xl text-gray-700 mb-6">
                "{testimonial.quote}"
              </blockquote>
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="font-semibold text-gray-900">
                    {testimonial.author}
                  </div>
                  <div className="text-gray-600">
                    {testimonial.role}, {testimonial.company}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
